import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { api } from '@/api/client';
import { isPublicTrackHost } from '@/utils/appHost';

type Theme = 'light' | 'dark' | 'system';
const THEME_KEY = 'cp.theme';

interface Ctx { theme: Theme; setTheme: (t: Theme) => void; resolved: 'light' | 'dark'; }
const ThemeCtx = createContext<Ctx>({ theme: 'system', setTheme: () => {}, resolved: 'light' });
export const useTheme = () => useContext(ThemeCtx);

function applyTheme(t: Theme): 'light' | 'dark' {
  // The public tracking page owns its own theme (see TrackApp) and defaults to light. This provider
  // wraps the whole bundle, so without this guard its 'system' default would paint the customer's
  // tracking page dark just because their phone is in dark mode.
  if (isPublicTrackHost()) return 'light';
  const sysDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const eff = t === 'system' ? (sysDark ? 'dark' : 'light') : t;
  document.documentElement.classList.toggle('dark', eff === 'dark');
  document.documentElement.setAttribute('data-theme', eff);
  return eff;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => (localStorage.getItem(THEME_KEY) as Theme) || 'system');
  const [resolved, setResolved] = useState<'light' | 'dark'>(() => applyTheme(theme));

  // Apply when theme changes
  useEffect(() => {
    const eff = applyTheme(theme);
    setResolved(eff);
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  // React to system change when in 'system'
  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const fn = () => setResolved(applyTheme('system'));
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, [theme]);

  // Fetch from server after login
  useEffect(() => {
    const t = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
    if (!t) return;
    api.get('/me/prefs').then(r => {
      const serverTheme = (r.data?.theme as Theme | undefined);
      if (serverTheme && serverTheme !== theme) setThemeState(serverTheme);
    }).catch(() => {});
  }, []);

  const setTheme = (t: Theme) => {
    setThemeState(t);
    // Best-effort sync to backend
    const tk = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
    if (tk) api.put('/me/prefs', { theme: t }).catch(() => {});
  };

  return <ThemeCtx.Provider value={{ theme, setTheme, resolved }}>{children}</ThemeCtx.Provider>;
}
